(() => {
  const CURSOR_PROGRESS_RADIUS = 24;
  const CURSOR_PROGRESS_CIRCUMFERENCE = 2 * Math.PI * CURSOR_PROGRESS_RADIUS;

  const manualPauseByBox = new WeakMap();
  const sectionObservers = new WeakMap();
  const boxResizeObservers = new WeakMap();
  const isSafariBrowser = () => {
    if (typeof navigator === "undefined") return false;
    const userAgent = navigator.userAgent;
    return /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|Edg/i.test(userAgent);
  };
  const getRealSlidesCount = (swiper) => {
    if (!swiper?.slides) return 0;

    return Array.from(swiper.slides).filter(
      (slide) => !slide.classList.contains("swiper-slide-duplicate")
    ).length;
  };

  const pauseCursorProgress = (box) => {
    if (!box) return;

    const progress = box.querySelector(".slideshow__cursor-progress svg circle");
    if (!progress) return;

    progress.style.animation = "none";
    progress.style.strokeDashoffset = getComputedStyle(progress).strokeDashoffset;
  };

  const startCursorProgress = (box) => {
    if (!box) return;

    const progress = box.querySelector(".slideshow__cursor-progress svg circle");
    if (!progress) return;

    const duration = progress
      .closest(".slideshow__cursor-progress")
      ?.style.getPropertyValue("--cursor-progress-duration")
      .trim();
    const animationDuration =
      duration || `${progress.getAttribute("data-animation-duration") || 0}ms`;

    progress.style.animation = "none";
    progress.style.strokeDashoffset = `${CURSOR_PROGRESS_CIRCUMFERENCE}`;
    void progress.offsetWidth;
    progress.style.animation = `cursor-progress ${animationDuration} linear infinite`;
  };

  const stopCursorProgress = (box, dashOffset = CURSOR_PROGRESS_CIRCUMFERENCE) => {
    if (!box) return;

    const progress = box.querySelector(".slideshow__cursor-progress svg circle");
    if (!progress) return;

    progress.style.animation = "none";
    progress.style.strokeDashoffset = `${dashOffset}`;
  };

  const playVideo = (slider) => {
    if (!slider.swiper) return;

    const swiper = slider.swiper;

    swiper.slides.forEach((slide) => {
      const video = slide.querySelector(".slideshow__video video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });

    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide) {
      const videoActive = activeSlide.querySelector(".slideshow__video video");
      if (videoActive) {
        videoActive.play().catch(() => {});
      }
    }
  };

  const stopVideo = (slider) => {
    if (!slider.swiper) return;

    const swiper = slider.swiper;

    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide) {
      const videoActive = activeSlide.querySelector(".slideshow__video video");
      if (videoActive) {
        videoActive.pause();
      }
    }
  };

  const getActiveSlideHeading = (swiper) => {
    if (!swiper?.slides?.length) return null;
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return null;
    return activeSlide.querySelector(".slideshow__title-content--animate");
  };

  const triggerHeadingAnimation = (swiper) => {
    const root = swiper?.el;
    if (!root) return;

    root.querySelectorAll(".slideshow__title-content--animate").forEach((el) => {
      el.classList.remove("animated");
    });

    const heading = getActiveSlideHeading(swiper);
    if (!heading) return;

    void heading.offsetWidth;
    heading.classList.add("animated");
  };

  const initSlider = (section) => {
    if (!section || !section.classList.contains("section-slideshow")) return;

    const box = section.querySelector(".slideshow");

    if (box) {
      box.classList.toggle("is-safari", isSafariBrowser());
      const isAutoplay = box.getAttribute("data-autoplay") === "true";
      const isLoop = box.getAttribute("data-loop") === "true";

      const swiperParams = {
        speed: 1000,
        autoHeight: false,
        allowTouchMove: true,
        watchSlidesProgress: true,
        preventInteractionOnTransition: true,
        loop: isLoop,
        mousewheel: {
          forceToAxis: true,
        },
      };

      const enableHeaderAnimation =
        box.getAttribute("data-enable-header-animation") === "true";

      const shouldAnimateCursorProgress = (swiper) => {
        if (!swiper || !isAutoplay) return false;
        if (getRealSlidesCount(swiper) < 2) return false;
        if (isLoop) return true;

        return !swiper.isEnd;
      };

      const shouldShowCursorProgress = (swiper) => {
        if (!isAutoplay || !swiper) return false;
        if (getRealSlidesCount(swiper) < 2) return false;
        if (isLoop) return true;

        return !swiper.isEnd;
      };

      const cursorProgressParent = box.querySelector(".slideshow__cursor");
      const cursorProgressEl = cursorProgressParent?.querySelector(
        ".slideshow__cursor-progress"
      );

      const syncCursorProgressElement = (swiper) => {
        if (!cursorProgressParent || !cursorProgressEl) return;

        if (shouldShowCursorProgress(swiper)) {
          if (!cursorProgressEl.isConnected) {
            cursorProgressParent.append(cursorProgressEl);
          }
          return;
        }

        cursorProgressEl.remove();
      };
      let progressSlideIndex = null;
      const isProgressAnimating = () => {
        const progress = box.querySelector(".slideshow__cursor-progress svg circle");
        if (!progress || !progress.isConnected) return false;

        const progressStyle = getComputedStyle(progress);
        const animationName = progressStyle.animationName || "";

        return (
          animationName !== "none" &&
          animationName.includes("cursor-progress") &&
          progressStyle.animationPlayState === "running"
        );
      };

      const syncCursorProgress = (swiper) => {
        syncCursorProgressElement(swiper);

        if (manualPauseByBox.get(box)) {
          progressSlideIndex = null;
          pauseCursorProgress(box);
          return;
        }

        const shouldAnimate = shouldAnimateCursorProgress(swiper);
        if (shouldAnimate) {
          const activeSlideIndex = swiper?.realIndex ?? swiper?.activeIndex ?? -1;
          if (progressSlideIndex === activeSlideIndex && isProgressAnimating()) return;

          progressSlideIndex = activeSlideIndex;
          startCursorProgress(box);
          return;
        }

        progressSlideIndex = null;
        stopCursorProgress(box);
      };

      if (isAutoplay) {
        swiperParams.autoplay = {
          disableOnInteraction: false,
          stopOnLastSlide: !isLoop,
          waitForTransition: false,
        };
      }

      if (box.getAttribute("data-pagination") === "true") {
        const paginationEl = box.querySelector(".swiper-pagination");

        swiperParams.pagination = {
          el: paginationEl,
          type: "bullets",
          clickable: true,
        };
      }

      if (box.getAttribute("data-parallax") === "true") {
        swiperParams.parallax = true;
      }

      if (box.getAttribute("data-animation-type") === "fade") {
        swiperParams.effect = "fade";
        swiperParams.fadeEffect = {
          crossFade: true,
        };
      } else if (box.getAttribute("data-animation-type") === "wipe") {
        swiperParams.effect = "slide";
        swiperParams.virtualTranslate = true;
      } else {
        swiperParams.effect = "slide";
      }

      if (box.getAttribute("data-full-width") === "false") {
        swiperParams.breakpoints = {
          990: {
            spaceBetween: 16,
          },
        };
      }

        const sliderOverlay = box.querySelector(".slideshow__swiper--overlay");

        if (sliderOverlay) {
          if (sliderOverlay.swiper) {
            sliderOverlay.swiper.destroy(true, true);
          }

          swiperParams.init = false;

          const toggleAutoplay = (swiper, button) => {
            if (!swiper?.autoplay || !button) return;

            const iconPlay = button.querySelector(".icon-play");
            const iconPause = button.querySelector(".icon-pause");
            const labelPause = button.dataset.labelPause;
            const labelPlay = button.dataset.labelPlay;
            const isPaused = manualPauseByBox.get(box);

            if (!isPaused) {
              swiper.autoplay.stop();
              manualPauseByBox.set(box, true);
              progressSlideIndex = null;
              button.classList.add("paused");
              iconPlay?.classList.remove("hidden");
              iconPause?.classList.add("hidden");
              button.setAttribute("aria-pressed", "false");
              if (labelPlay) button.setAttribute("aria-label", labelPlay);
              pauseCursorProgress(box);
            } else {
              swiper.autoplay.start();
              manualPauseByBox.set(box, false);
              playVideo(sliderOverlay);
              button.classList.remove("paused");
              iconPlay?.classList.add("hidden");
              iconPause?.classList.remove("hidden");
              button.setAttribute("aria-pressed", "true");
              if (labelPause) button.setAttribute("aria-label", labelPause);
              syncCursorProgress(swiper);
            }
          };

          const swiperOverlay = new Swiper(sliderOverlay, swiperParams);

          const autoplayButton = box.querySelector(".slideshow-autoplay__button");
          autoplayButton?.addEventListener("click", () =>
            toggleAutoplay(swiperOverlay, autoplayButton)
          );

          swiperOverlay.on("slideChange", () => {
            progressSlideIndex = null;
            playVideo(sliderOverlay);
            syncCursorProgress(swiperOverlay);
          });

          swiperOverlay.on("slideChangeTransitionStart", () => {
            if (enableHeaderAnimation) {
              triggerHeadingAnimation(swiperOverlay);
            }
          });

          swiperOverlay.on("init", () => {
            playVideo(sliderOverlay);
            requestAnimationFrame(() => {
              syncCursorProgress(swiperOverlay);
            });
            if (enableHeaderAnimation) {
              requestAnimationFrame(() => {
                triggerHeadingAnimation(swiperOverlay);
              });
            }
          });

          swiperOverlay.on("autoplayPause", () => {
            progressSlideIndex = null;
            pauseCursorProgress(box);
          });

          swiperOverlay.on("autoplayResume", () => {
            if (!manualPauseByBox.get(box)) {
              if (!isProgressAnimating()) {
                syncCursorProgress(swiperOverlay);
              }
            }
          });

          if (box.getAttribute("data-animation-type") === "wipe") {
            const syncWipeProgress = (swiper) => {
              swiper.slides.forEach((slide) => {
                const wipeProgress = Math.max(-1, Math.min(1, slide.progress));
                slide.style.transform = `translateX(${-slide.swiperSlideOffset}px)`;
                slide.style.setProperty("--slide-progress", wipeProgress);
              });
            };

            let wipeTransitionTimer = null;

            swiperOverlay.on("progress", () => syncWipeProgress(swiperOverlay));
            swiperOverlay.on("init", () => syncWipeProgress(swiperOverlay));

            swiperOverlay.on("setTransition", (swiper, duration) => {
              swiper.slides.forEach((slide) => {
                slide.style.setProperty("--wipe-duration", `${duration}ms`);
              });
            });

            swiperOverlay.on("beforeLoopFix", () => {
              swiperOverlay.slides.forEach((slide) => {
                slide.style.setProperty("--wipe-duration", "0ms");
              });
            });

            swiperOverlay.on("loopFix", () => {
              syncWipeProgress(swiperOverlay);
              // Commit the snapped clip-path state before Swiper restores the
              // transition duration for the real animated part of the jump.
              void swiperOverlay.wrapperEl.offsetWidth;
            });

            swiperOverlay.on("transitionStart", () => {
              clearTimeout(wipeTransitionTimer);
              wipeTransitionTimer = setTimeout(() => {
                swiperOverlay.transitionEnd(true);
              }, swiperOverlay.params.speed);
            });
          }

          swiperOverlay.init();
        }
    }
  };

  const initCursor = (section) => {
    if (!section || !section.classList.contains("section-slideshow")) return;

    const box = section.querySelector(".slideshow");
    const isNavigationEnabled =
      box?.getAttribute("data-cursor-arrows") === "true";
    const slider = section.querySelector(".slideshow__swiper--overlay");
    const cursorEl = section.querySelector(".slideshow__cursor");
    if (!box || !isNavigationEnabled || !slider?.swiper || !cursorEl) return;
    if (getRealSlidesCount(slider.swiper) < 2) {
      box.classList.remove("cursor-active");
      return;
    }

    const blockedTargets = [
      ".slideshow__description-content a",
      ".slideshow__buttons-content .button",
      ".slideshow__pagination",
      ".slideshow-autoplay__button",
    ];

    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;
    let isAnimating = false;
    const easing = 0.2;
    let isCursorInit = false;
    let lastPointerClientX = null;
    const getInteractiveRect = () => {
      const interactiveArea =
        box.querySelector(".slideshow__container") ||
        box.querySelector(".slideshow__swiper--overlay") ||
        box;
      return interactiveArea.getBoundingClientRect();
    };
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
    const isInsideInteractiveArea = (clientX, clientY) => {
      const rect = getInteractiveRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    };
    const getCursorCoordinates = (event) => {
      const contentRect = getInteractiveRect();
      const cursorRect = cursorEl.getBoundingClientRect();
      const rawX = event.clientX - contentRect.left - cursorRect.width / 2;
      const rawY = event.clientY - contentRect.top - cursorRect.height / 2;
      const maxX = Math.max(0, contentRect.width - cursorRect.width);
      const maxY = Math.max(0, contentRect.height - cursorRect.height);

      return {
        x: clamp(rawX, 0, maxX),
        y: clamp(rawY, 0, maxY),
      };
    };

    const getCursorState = (clientX) => {
      const swiper = slider.swiper;
      if (!swiper) {
        return {
          direction: "next",
          canNavigate: true,
        };
      }

      const contentRect = getInteractiveRect();
      const pointerX =
        typeof clientX === "number" ? clientX : contentRect.left + contentRect.width / 2;
      const isLeftHalf = pointerX < contentRect.left + contentRect.width / 2;
      const isLoop = box.getAttribute("data-loop") === "true";

      const wantsPrev = swiper.rtl ? !isLeftHalf : isLeftHalf;
      const direction = wantsPrev ? "prev" : "next";
      const canNavigate = isLoop
        ? true
        : wantsPrev
          ? !swiper.isBeginning
          : !swiper.isEnd;

      return {
        direction,
        canNavigate,
      };
    };

    const syncCursorState = (clientX) => {
      const { direction, canNavigate } = getCursorState(clientX);
      cursorEl.classList.toggle("prev", direction === "prev");
      cursorEl.classList.toggle("next", direction === "next");
      cursorEl.classList.toggle("slideshow__cursor--disabled", !canNavigate);
      return {
        direction,
        canNavigate,
      };
    };

    const animateCursor = () => {
      isAnimating = true;

      currentX += (targetX - currentX) * easing;
      currentY += (targetY - currentY) * easing;

      cursorEl.style.left = `${currentX}px`;
      cursorEl.style.top = `${currentY}px`;

      if (
        Math.abs(targetX - currentX) > 0.1 ||
        Math.abs(targetY - currentY) > 0.1
      ) {
        requestAnimationFrame(animateCursor);
      } else {
        isAnimating = false;
      }
    };

    const handleMouseMove = (event) => {
      const isBlocked = blockedTargets.some((selector) =>
        event.target.closest(selector)
      );
      if (isBlocked) {
        box.classList.remove("cursor-active");
        return;
      }

      if (!isInsideInteractiveArea(event.clientX, event.clientY)) {
        box.classList.remove("cursor-active");
        return;
      }

      box.classList.add("cursor-active");
      lastPointerClientX = event.clientX;
      syncCursorState(lastPointerClientX);
      const { x, y } = getCursorCoordinates(event);
      targetX = x;
      targetY = y;

      if (!isAnimating) {
        animateCursor();
      }
    };

    const handleMouseEnter = (event) => {
      if (!isInsideInteractiveArea(event.clientX, event.clientY)) {
        return;
      }

      const { x, y } = getCursorCoordinates(event);
      currentX = x;
      currentY = y;
      targetX = currentX;
      targetY = currentY;

      lastPointerClientX = event.clientX;
      syncCursorState(lastPointerClientX);

      cursorEl.style.left = `${currentX}px`;
      cursorEl.style.top = `${currentY}px`;
      box.classList.add("cursor-active");
    };

    const handleMouseLeave = () => {
      box.classList.remove("cursor-active");
    };

    const handleClick = (event) => {
      const isBlocked = blockedTargets.some((selector) =>
        event.target.closest(selector)
      );
      if (isBlocked) return;

      if (!isInsideInteractiveArea(event.clientX, event.clientY)) {
        return;
      }

      const swiper = slider.swiper;
      if (!swiper || swiper.animating) return;

      const { direction, canNavigate } = syncCursorState(event.clientX);
      if (!canNavigate) return;

      if (direction === "next") {
        swiper.slideNext();
      } else {
        swiper.slidePrev();
      }
    };

    boxResizeObservers.get(box)?.disconnect();

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const canUseCursor =
          entry.contentRect.width >= 750 &&
          window.matchMedia("(hover: hover) and (pointer: fine)").matches;

        if (canUseCursor && !isCursorInit) {
          box.addEventListener("mouseenter", handleMouseEnter);
          box.addEventListener("mousemove", handleMouseMove);
          box.addEventListener("mouseleave", handleMouseLeave);
          box.addEventListener("click", handleClick);
          syncCursorState(lastPointerClientX);
          isCursorInit = true;
        } else if (!canUseCursor && isCursorInit) {
          box.removeEventListener("mouseenter", handleMouseEnter);
          box.removeEventListener("mousemove", handleMouseMove);
          box.removeEventListener("mouseleave", handleMouseLeave);
          box.removeEventListener("click", handleClick);
          box.classList.remove("cursor-active");
          isCursorInit = false;
        }
      });
    });

    boxResizeObservers.set(box, resizeObserver);
    resizeObserver.observe(box);

    slider.swiper.on("init", () => {
      syncCursorState(lastPointerClientX);
    });

    slider.swiper.on("slideChange", () => {
      syncCursorState(lastPointerClientX);
    });

    syncCursorState(lastPointerClientX);
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains("section-slideshow")) return;

    const box = section.querySelector(".slideshow");

    const slider = section.querySelector(".slideshow__swiper");
    if (!slider) return;

    sectionObservers.get(section)?.disconnect();

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (slider) {
            playVideo(slider);
            if (
              slider.swiper &&
              box &&
              box.getAttribute("data-autoplay") === "true" &&
              !manualPauseByBox.get(box)
            ) {
              slider.swiper.autoplay.resume();
            }
          }
        } else {
          if (slider) {
            stopVideo(slider);
            if (
              slider.swiper &&
              box &&
              box.getAttribute("data-autoplay") === "true"
            ) {
              slider.swiper.autoplay.pause();
            }
          }
        }
      });
    });

    sectionObservers.set(section, sectionObserver);
    sectionObserver.observe(section);
  };

  initSlider(document.currentScript.parentElement);
  initSection(document.currentScript.parentElement);
  initCursor(document.currentScript.parentElement);
  

  document.addEventListener("shopify:section:load", (event) => {
    initSlider(event.target);
    initSection(event.target);
    initCursor(event.target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target;
    if (!section.classList.contains("section-slideshow")) return;

    const sliderOverlay = section.querySelector(".slideshow__swiper--overlay");
    if (!sliderOverlay?.swiper) return;

    const swiper = sliderOverlay.swiper;
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const slide = section.querySelector(`.slideshow__slide-${blockId}`);
    if (!slide) return;

    const realSlides = Array.from(swiper.slides).filter(
      (s) => !s.classList.contains("swiper-slide-duplicate")
    );
    const slideIndex = realSlides.indexOf(slide);

    if (slideIndex !== -1) {
      swiper.slideToLoop(slideIndex);
      swiper.autoplay?.stop();
    }
  });

  document.addEventListener("shopify:block:deselect", (event) => {
    const section = event.target;
    if (!section.classList.contains("section-slideshow")) return;

    const box = section.querySelector(".slideshow");
    const sliderOverlay = section.querySelector(".slideshow__swiper--overlay");
    if (!sliderOverlay?.swiper || !box) return;

    if (
      box.getAttribute("data-autoplay") === "true" &&
      !manualPauseByBox.get(box)
    ) {
      sliderOverlay.swiper.autoplay?.start();
    }
  });
})();
